import { useEffect, useState } from 'react';
import styles from './ThreadImage.module.css';

const TOKEN_KEY = 'foreman-auth-token';

interface ThreadImageProps {
  sessionKey: string;
  b64len: number;
  mimeType: string;
}

/** Lazy-loads one inline photo from a chat thread. chat.history omits image
 *  bytes, so we fetch the actual image from the ACL-gated /api/chat/thread-image
 *  endpoint (matched by base64 length) and render it. Clicking opens the
 *  full-size image in a new tab (view / print / save). */
export function ThreadImage({ sessionKey, b64len, mimeType }: ThreadImageProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sessionKey) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const q = new URLSearchParams({
          session: sessionKey,
          b64len: String(b64len),
          mime: mimeType,
        });
        const res = await fetch(`/api/auth/thread-image?${q.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sessionKey, b64len, mimeType]);

  if (failed) return <div className={styles.placeholder}>{'\u{1F4F7}'} photo unavailable</div>;
  if (!url) return <div className={styles.placeholder}>{'\u{1F4F7}'} loading photo…</div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className={styles.link} title="Open full size">
      <img src={url} className={styles.img} alt="attachment" loading="lazy" />
    </a>
  );
}
